import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { episodeStates } from '../../../common/constants/appConstants'
import { CLIENT_NEW_NOTE } from '../../../common/constants/routingConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useLazyGetContentsByProgramIdQuery } from '../../../lyraTherapy/data/contentsMetadataApi'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import { useLazyGetClientEpisodesQuery } from '../../../lyraTherapy/episodes/data/episodesApi'

export const useNavigateToGeneralNote = () => {
  const { formatMessage } = useIntl()
  const userId = useSelector(getAuthUserId)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [getClientEpisodes] = useLazyGetClientEpisodesQuery()
  const [getContentsByProgramId] = useLazyGetContentsByProgramIdQuery()

  const showErrorToast = useCallback(() => {
    dispatch(
      setToastContent({
        text: formatMessage({
          defaultMessage: 'Unable to navigate to note. Please try again.',
          description: 'Error message when navigating to general note fails',
        }),
        id: 'navigate-to-general-note-error',
        toastType: 'error',
      }),
    )
  }, [dispatch, formatMessage])

  const navigateToGeneralNote = useCallback(
    async (clientId: string, originRoute = 'riskAlerts') => {
      try {
        const episodes = await getClientEpisodes({ providerId: userId, patientId: clientId }).unwrap()
        const episode = episodes?.find((e) => e.state === episodeStates.IN_PROGRESS) ?? episodes?.[0]
        if (!episode) {
          showErrorToast()
          return
        }

        const contents = await getContentsByProgramId({ programId: episode.program_id }).unwrap()
        if (!contents) {
          showErrorToast()
          return
        }

        const { GENERAL_NOTE } = Programs.getProgramConfigByName(episode.program_name)?.noteContentMetadataName ?? {}

        const generalNoteMetadata = contents.find((c) => c.name === GENERAL_NOTE)
        if (!generalNoteMetadata) {
          showErrorToast()
          return
        }

        dispatch(setSelectedClientId(clientId))
        navigate(CLIENT_NEW_NOTE.route, {
          state: {
            note: {
              title: generalNoteMetadata.title,
              content_meta_data: generalNoteMetadata,
              note_type: NoteTypes.GENERAL,
            },
            originRoute,
          },
        })
      } catch {
        showErrorToast()
      }
    },
    [dispatch, getClientEpisodes, getContentsByProgramId, navigate, showErrorToast, userId],
  )

  return { navigateToGeneralNote }
}
